/**
 * Where to send somebody back to after they sign in.
 *
 * The rule the acceptance criterion actually needs: arriving at a protected screen
 * without a session must remember where you were going, and signing in must put you
 * there rather than on a dashboard you did not ask for.
 *
 * **A return URL is user input.** It arrives in a query string, so anyone can put
 * anything in it. An absolute URL there is an open redirect — a link that looks like
 * ours, lands on a convincing copy of the login screen somewhere else, and collects
 * the password. So only same-origin paths survive: it must start with a single
 * slash, and `//evil.example` (a protocol-relative URL) must not.
 */

const DEFAULT_PATH = '/';

/** The path to send an unauthenticated visitor back to, if it is safe to. */
export function safeReturnPath(next: string | undefined): string {
  if (!next) return DEFAULT_PATH;
  if (!next.startsWith('/')) return DEFAULT_PATH;
  // `//host` and `/\host` are both read as protocol-relative by browsers.
  if (next.startsWith('//') || next.startsWith('/\\')) return DEFAULT_PATH;
  // Never bounce straight back to an auth screen — that is a loop, not a return.
  if (AUTH_PATHS.some((path) => next === path || next.startsWith(`${path}?`))) {
    return DEFAULT_PATH;
  }
  return next;
}

export const AUTH_PATHS: readonly string[] = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/verify-code',
  '/session-expired',
  '/account-locked',
];
